/**
 * `heysegment status` — is the session's version still the working version?
 *
 * Read-only cross-version check (distinct from `check`, which is the
 * within-version safe-to-push verdict). Prints the bound version's lifecycle
 * status and, when it has been superseded, points at `heysegment switch`.
 *
 * @module
 */
import { Console, Effect } from "effect"
import { Command } from "effect/unstable/cli"
import type { VersionStatus } from "@repo/contracts/rpc/procedures/session"
import { withSession, step } from "../session/with-session"
import { SessionClient } from "../rpc"
import { apiUrlFlag, dirFlag } from "./flags"

const versionLabel = (n: number | null): string => (n === null ? "?" : `v${n}`)

/** The human summary line(s) for a version-status verdict. */
const render = (s: VersionStatus): ReadonlyArray<string> => {
  const bound = `version ${versionLabel(s.boundVersionNumber)} — ${s.boundStatus}${
    s.boundFrozen ? " (frozen)" : ""
  }`

  if (s.recommendation === "ok") {
    return [`${bound} — you're on the current working version, up to date.`]
  }
  if (s.recommendation === "pull") {
    return [
      `${bound} — the working version, but it advanced since you pulled.`,
      "→ run `heysegment pull` to take the latest.",
    ]
  }
  // switch
  const working =
    s.workingVersionId === null
      ? "no editable version exists yet"
      : `the current working version is ${versionLabel(s.workingVersionNumber)}`
  return [
    `${bound} — ${working}.`,
    "→ run `heysegment switch` to move to the current working version and continue.",
  ]
}

export const statusCommand = Command.make(
  "status",
  { dir: dirFlag, apiUrl: apiUrlFlag },
  (flags) =>
    withSession({ dir: flags.dir, apiUrl: flags.apiUrl }, () =>
      Effect.gen(function* () {
        const client = yield* SessionClient
        const status = yield* step("status", client.getVersionStatus(void 0))
        for (const line of render(status)) {
          yield* Console.log(line)
        }
      }),
    ),
).pipe(
  Command.withDescription(
    "Show whether the session is on the workspace's current working version",
  ),
)
